import type { RequestStatus, SlownessBucket } from "@gw/shared-types";

export function slownessLabel(bucket: SlownessBucket): string {
  switch (bucket) {
    case "OnTrack":
      return "On Track";
    case "Slow":
      return "Slow";
    case "Critical":
      return "Critical";
    case "InsufficientData":
      return "Insufficient Data";
    case "InProgress":
      return "In Progress";
  }
}

// Concept-level explanation for the Slowness column header tooltip. Plain language, no jargon —
// keep aligned with SlownessScorer.cs (thresholds) and PeerGroupService.cs (peer group + ≥5).
export const SLOWNESS_CONCEPT =
  "How long this request took vs. the median of similar completed requests " +
  "(same client, line of business, and engagement type, with at least 5 peers). " +
  "≥1.25× = Slow, ≥2.0× = Critical. Scored only once a request completes.";

// Structured per-pill tooltip content. The component renders this as a styled card: the "scored"
// shape gets the bold multiplier + an optional peer line + a constant threshold legend; the
// "message" shape is a single explanatory sentence (in-progress / not enough peers).
export type SlownessTooltip =
  | { kind: "scored"; ratio: number; peer: string | null }
  | { kind: "message"; text: string };

// ratio/peerMedian/peerCount are paired on the wire — when ratio is non-null the peer fields are
// present — but we degrade gracefully (peer: null) if they're missing.
export function slownessTooltip(
  bucket: SlownessBucket,
  ratio: number | null,
  peerMedian?: number,
  peerCount?: number,
  unit: "bd" | "cd" = "bd",
): SlownessTooltip {
  if (bucket === "InProgress") {
    return {
      kind: "message",
      text: "Still in progress — slowness is only scored once a request completes.",
    };
  }
  if (bucket === "InsufficientData" || ratio == null) {
    return {
      kind: "message",
      text: "Not enough comparable completed requests (we need at least 5) to score this one against its peers.",
    };
  }
  const peer =
    peerMedian != null
      ? `Peer median ${peerMedian.toFixed(1)} ${unit}` +
        (peerCount != null ? ` across ${peerCount} comparable requests` : "") +
        "."
      : null;
  return { kind: "scored", ratio, peer };
}

export function statusLabel(status: RequestStatus): string {
  switch (status) {
    case "Completed":
      return "Completed";
    case "InProgress":
      return "In Progress";
    case "Draft":
      return "Draft";
    case "PendingValidation":
      return "Pending Validation";
    case "RequiresReview":
      return "Requires Review";
    case "PendingOnboarding":
      return "Pending Onboarding";
    case "OnHold":
      return "On Hold";
    case "Stalled":
      return "Stalled";
    case "AwaitingConsultant":
      return "Awaiting Consultant";
    case "Discarded":
      return "Discarded";
    case "Cancelled":
      return "Cancelled";
    case "Unknown":
      return "Unknown";
  }
}
